export const blogEn = {
  blog: {
    backToBlog: "Back to Blog",
    blogTitle: "Sentinel Crop Blog",
    blogSubtitle: "Expert insights on parametric insurance, satellite monitoring, and precision agriculture. Stay informed with the latest in crop protection technology.",
    featuredArticles: "Featured Articles",
    allArticles: "All Articles",
    featured: "Featured",
    minRead: "min read",
    configureAlerts: "Configure Alerts",
    viewIrrigationRecs: "View Irrigation Recommendations",
    viewFieldNotebook: "View Field Notebook",
    protectFromFrost: "Protect your crops from frost",
    protectFromFrostDesc: "Set up personalized alerts for your parcels and receive warnings up to 72 hours in advance.",
    optimizeIrrigation: "Optimize your irrigation with science",
    optimizeIrrigationDesc: "Receive daily irrigation recommendations based on FAO-56 methodology and AEMET weather data for your parcel.",
    digitizeNotebook: "Digitize your field notebook",
    digitizeNotebookDesc: "Comply with regulations and improve farm management with satellite-validated records.",
    // Blog Index post data
    posts: {
      parametricInsurance: {
        title: "What is Parametric Insurance? Complete Guide for Farmers",
        description: "Learn how parametric insurance works with automatic payouts based on weather indices and satellite data, backed by World Bank and FAO research.",
        category: "Insurance Guides"
      },
      satelliteMonitoring: {
        title: "Satellite Crop Monitoring: The Science Behind NDVI Analysis",
        description: "Discover how Copernicus Sentinel-2 satellites provide 10-meter resolution NDVI data every 5 days for precision agriculture and early stress detection.",
        category: "Technology"
      },
      successStory: {
        title: "Case Study: Spanish Olive Farm Receives €45,000 Payout in 48 Hours",
        description: "Real success story of how parametric insurance with satellite verification enabled automatic hail damage compensation without traditional claims process.",
        category: "Success Stories"
      },
      frostProtection: {
        title: "Frost Protection Alerts: Early Warning Systems for Crop Protection",
        description: "How microclimate modeling and AEMET weather data provide 72-hour frost predictions with temperature accuracy within ±1.5°C for vineyards and orchards.",
        category: "Features"
      },
      smartIrrigation: {
        title: "Smart Irrigation with FAO-56: Scientific Water Management",
        description: "Understanding the FAO-56 Penman-Monteith equation for calculating crop evapotranspiration and optimizing irrigation schedules. Literature reports 20-30% water savings vs over-irrigation; actual results vary per farm.",
        category: "Features"
      },
      fieldNotebook: {
        title: "Digital Field Notebook: Complete Farm Activity Traceability",
        description: "How satellite-validated activity logging creates verifiable records for EU CAP compliance, organic certification, and ROI tracking of farm interventions.",
        category: "Features"
      },
      variableRate: {
        title: "Variable Rate Fertilization: Precision Nutrient Management",
        description: "Using NDVI-based prescription maps to optimize fertilizer application. Literature suggests 15-25% input reduction; actual savings depend on baseline practices and have not been independently validated by Sentinel Crop.",
        category: "Features"
      },
      reports: {
        title: "Professional Agricultural Reports for Banks and Insurance",
        description: "Generate satellite-verified crop reports with NDVI timelines, activity logs, and compliance documentation for financing and insurance applications.",
        category: "Features"
      },
      fireRisk: {
        title: "Fire-Risk Monitoring with NASA FIRMS",
        description: "Satellite-derived fire-risk alerts using NASA FIRMS hotspot data, with automatic notifications when satellite hotspots are detected near your parcels. Not an emergency service.",
        category: "Technology"
      },
      floodRisk: {
        title: "Flood Risk Assessment with Copernicus Global Surface Water",
        description: "Using 40 years of Landsat data to assess historical water occurrence and flood risk for agricultural parcels.",
        category: "Technology"
      },
      carbon: {
        title: "Carbon Monitoring & Credit Tokenization",
        description: "How satellite-based biomass carbon estimation enables verifiable carbon credits and blockchain tokenization.",
        category: "Sustainability"
      }
    },
    // WhatIsParametricInsurance
    parametric: {
      seoTitle: "What is Parametric Insurance? Complete Guide for Farmers",
      seoDesc: "Learn how parametric insurance works, why it's revolutionizing crop protection, and how automatic payouts can save your farm from weather-related losses.",
      badge: "Insurance Guides",
      title: "What is Parametric Insurance? A Complete Guide for Farmers",
      subtitle: "Learn how parametric insurance works, why it's revolutionizing crop protection, and how automatic payouts can save your farm from weather-related losses.",
      date: "December 20, 2025",
      readTime: "8 min read",
      intro: "Traditional crop insurance has served farmers for decades, but it comes with significant drawbacks: lengthy claims processes, disputes over damage assessments, and payouts that often arrive too late. Parametric insurance is changing everything.",
      understandingTitle: "Understanding Parametric Insurance",
      understandingP1: "Parametric insurance (also known as index-based insurance) is a revolutionary approach to risk protection that triggers automatic payouts when specific, measurable conditions occur—regardless of actual damage assessment.",
      understandingP2: "Instead of waiting for an insurance adjuster to evaluate your crops after a frost event, parametric insurance uses objective data from weather stations, satellites, and other sensors to determine if a payout is warranted.",
      howItWorksTitle: "How It Works: A Simple Example",
      howItWorksDesc: "You purchase frost coverage with a trigger temperature of -2°C. On April 15th, the official weather station records -4°C for 3 consecutive hours. Within 48 hours, you receive your payout automatically—no claims forms, no inspections, no waiting.",
      vsTitle: "Parametric vs. Traditional Insurance",
      traditionalTitle: "Traditional Insurance",
      traditionalItems: [
        "Requires damage assessment",
        "Claims can take weeks or months",
        "Disputes over damage valuation",
        "High administrative costs",
        "Subjective evaluation process"
      ],
      parametricTitle: "Parametric Insurance",
      parametricItems: [
        "Automatic payout triggers",
        "Payouts within 24-72 hours",
        "Transparent, objective criteria",
        "Lower administrative overhead",
        "Data-driven decisions"
      ],
      triggersTitle: "What Events Can Trigger Payouts?",
      triggersIntro: "Parametric crop insurance can cover virtually any measurable weather event. Common triggers include:",
      triggerFrost: "Temperature drops below critical thresholds",
      triggerDrought: "Rainfall below minimum levels for extended periods",
      triggerRain: "Precipitation exceeding maximum thresholds",
      triggerHail: "Verified hail events with specific intensity measurements",
      triggerHeat: "Temperatures above crop tolerance limits",
      triggerWind: "Wind speeds exceeding defined thresholds",
      satelliteTitle: "The Role of Satellite Technology",
      satelliteP1: "Modern parametric insurance relies heavily on satellite data for verification. At Sentinel Crop, we use Copernicus Sentinel-2 satellites to monitor crop health through NDVI (Normalized Difference Vegetation Index) analysis.",
      satelliteP2: "This means we can verify crop stress and damage objectively, providing an additional layer of validation beyond simple weather measurements. The satellite imagery creates a timestamped, hash-traceable record of your field's condition before and after weather events.",
      benefitsTitle: "Benefits for Farmers",
      benefitSpeed: "Receive funds when you need them most—right after a damaging event, not months later.",
      benefitTransparency: "Know exactly what conditions will trigger your coverage, with no room for interpretation.",
      benefitCost: "Reduced administrative overhead means more competitive premiums.",
      benefitBlockchain: "Selected policy and claim records can be optionally anchored on a public ledger as an independent timestamp.",
      benefitFlexibility: "Customize coverage to match your specific crops and regional risks.",
      rightForYouTitle: "Is Parametric Insurance Right for Your Farm?",
      rightForYouIntro: "Parametric insurance works best for farmers who:",
      rightForYouItems: [
        "Have experienced delays with traditional insurance claims",
        "Need quick access to funds after weather events",
        "Want transparent, predictable coverage",
        "Grow crops sensitive to specific weather conditions",
        "Value data-driven decision making"
      ],
      ctaTitle: "Ready to Protect Your Crops?",
      ctaDesc: "Join thousands of European farmers who have switched to parametric insurance with Sentinel Crop. Get a personalized quote in minutes.",
      ctaButton: "View Pricing Plans",
      faqTitle: "Frequently Asked Questions",
      faq1Q: "What is parametric insurance?",
      faq1A: "Parametric insurance is a type of coverage that pays out automatically when predefined conditions (parameters) are met, such as temperature dropping below a certain threshold or rainfall exceeding a specific amount. Unlike traditional insurance, it doesn't require damage assessment or claims adjusters.",
      faq2Q: "How fast are parametric insurance payouts?",
      faq2A: "Parametric insurance payouts are typically processed within 24-72 hours after the triggering event is verified. With Sentinel Crop, most payouts are completed within 48 hours thanks to automated satellite verification and blockchain-based smart contracts.",
      faq3Q: "What weather events does parametric crop insurance cover?",
      faq3A: "Parametric crop insurance typically covers frost, drought, excess rainfall, hail, and extreme temperatures. Each policy defines specific thresholds that trigger automatic payouts when measured conditions exceed these limits."
    },
    // SatelliteMonitoringAgriculture
    satellite: {
      badge1: "Technology",
      badge2: "Featured",
      title: "How Satellite Monitoring is Transforming Modern Agriculture",
      subtitle: "Explore how Copernicus Sentinel-2 satellites provide real-time crop health data, NDVI analysis, and early damage detection for precision farming.",
      date: "December 15, 2025",
      readTime: "7 min read",
      intro: "From 786 kilometers above Earth, the Copernicus Sentinel-2 satellites capture detailed images of agricultural land every 5 days. This data is revolutionizing how farmers monitor crops, detect problems, and make decisions.",
      powerTitle: "The Power of Satellite Imagery in Agriculture",
      powerDesc: "Satellite monitoring provides farmers with a \"bird's eye view\" that was impossible just a decade ago. Instead of walking fields to spot problems, you can now see your entire operation in a single image—with data that reveals far more than the human eye can detect.",
      sentinel2Title: "Sentinel-2 Satellites",
      sentinel2Desc: "Twin satellites providing 10m resolution imagery with 13 spectral bands, covering all of Europe every 5 days.",
      multispectralTitle: "Multispectral Analysis",
      multispectralDesc: "Captures visible light plus near-infrared bands invisible to humans, revealing plant stress before visible symptoms appear.",
      ndviTitle: "NDVI Indices",
      ndviDesc: "Normalized Difference Vegetation Index quantifies plant health on a scale from -1 to +1, enabling objective crop assessment.",
      insuranceVerifTitle: "Insurance Verification",
      insuranceVerifDesc: "Satellite data creates a timestamped, hash-traceable record of crop conditions before and after weather events to support objective claim review.",
      ndviRangesTitle: "NDVI Value Ranges",
      ndviDense: "Dense, healthy vegetation",
      ndviModerate: "Moderate vegetation health",
      ndviSparse: "Sparse vegetation or stress",
      ndviBare: "Bare soil or severe stress",
      ndviWater: "Water, snow, or clouds",
      practicalTitle: "Practical Applications for Farmers",
      earlyDetectionTitle: "1. Early Problem Detection",
      earlyDetectionDesc: "Satellite imagery can detect plant stress 7-14 days before symptoms become visible to the naked eye. This early warning gives farmers time to investigate and address issues before they spread.",
      precisionIrrigTitle: "2. Precision Irrigation",
      precisionIrrigDesc: "By identifying areas with lower NDVI values, farmers can target irrigation to specific zones rather than watering entire fields uniformly. This saves water and reduces costs while maintaining optimal crop health.",
      variableRateTitle: "3. Variable Rate Applications",
      variableRateDesc: "NDVI maps enable variable rate fertilizer and pesticide applications. Healthy areas receive less input, while stressed areas get targeted treatment—reducing chemical use and environmental impact.",
      yieldEstTitle: "4. Yield Estimation",
      yieldEstDesc: "Historical NDVI data correlated with actual yields allows for accurate yield predictions weeks before harvest. This helps with planning, marketing, and financial decisions.",
      insuranceClaimsTitle: "Satellite Data in Insurance Claims",
      insuranceClaimsP1: "One of the most powerful applications of satellite monitoring is in insurance verification. At Sentinel Crop, we use Sentinel-2 imagery to:",
      insuranceClaimsItems: [
        "Establish baseline crop health before weather events",
        "Document damage extent after frost, drought, or hail",
        "Verify that trigger conditions align with crop stress",
        "Provide objective evidence for faster claim processing"
      ],
      insuranceClaimsP2: "This satellite-based verification eliminates disputes about damage extent and enables payouts within 48 hours of verified events—a process that traditionally took weeks or months.",
      futureTitle: "The Future of Satellite Agriculture",
      futureIntro: "As satellite technology advances, we expect even more capabilities:",
      futureItems: [
        "New satellites offering 3m and even sub-meter resolution",
        "Constellation expansion providing daily coverage",
        "Machine learning algorithms predicting yields and detecting diseases",
        "Detailed spectral analysis for nutrient deficiency detection"
      ],
      ctaTitle: "See Your Fields from Space",
      ctaDesc: "Experience the power of satellite monitoring with Sentinel Crop. Get real-time NDVI analysis, weather alerts, and automatic insurance verification for your farm.",
      ctaButton: "Explore Features"
    },
    // SuccessStorySpanishOliveFarm
    oliveFarm: {
      backToBlog: "Back to Blog",
      disclaimerTitle: "Illustrative Scenario",
      disclaimerDesc: "This article presents a hypothetical example to illustrate how parametric insurance works. Names, figures and dates are fictional and used only for educational purposes. It does not represent a real case.",
      badge1: "Illustrative Scenario",
      badge2: "Educational",
      title: "How Parametric Insurance Works: Example for Olive Farms",
      subtitle: "Illustrative example of how an olive farm could receive an automatic payout in 48 hours after satellite verification of hail damage.",
      date: "Updated: December 2025",
      readTime: "5 min read",
      examplePayout: "Example Payout*",
      targetTime: "Target Time",
      exampleSize: "Example Size",
      scenarioTitle: "Scenario: A Typical Olive Farm",
      scenarioP1: "Imagine a 75-hectare olive farm in the province of Jaén, Andalucía—the heart of olive oil production in Spain. Like many family farms, this one produces high-quality extra virgin olive oil.",
      scenarioP2: "Olive farmers frequently experience losses from weather events. With traditional insurance, claims can take months to process, and disputes over damage assessment are common.",
      traditionalProblemTitle: "The problem with traditional insurance:",
      traditionalProblemItems: [
        "Claims processes lasting 3-8 months",
        "Frequent disputes over damage assessment",
        "Need to take loans while waiting for payment",
        "Extensive documentation and expert visits"
      ],
      challengeTitle: "The Challenge: Unpredictable Hailstorms",
      challengeP1: "In spring, hailstorms can sweep through the Jaén region. A storm with golf ball-sized hailstones for 20 minutes can cause significant damage to olive trees just as they enter their crucial flowering period.",
      challengeP2: "With conventional insurance, this would mean:",
      challengeItems: [
        "Filing detailed claims with photos and documentation",
        "Waiting weeks for an adjuster's visit",
        "Negotiating over the extent of damage",
        "Waiting months for claim resolution"
      ],
      solutionTitle: "The Parametric Insurance Solution",
      solutionIntro: "With Sentinel Crop's parametric insurance, parameters are defined in advance:",
      solutionTrigger: "Verified hail event with intensity ≥15mm",
      solutionCoverage: "€600 per hectare for affected area",
      solutionVerification: "Weather radar data + NDVI satellite confirmation",
      processTitle: "How It Would Work: 48-Hour Process",
      timeline: [
        { time: "Day 1, 14:30", title: "Hailstorm Detected", desc: "AEMET weather radar records hail event with 22mm intensity in the Jaén region." },
        { time: "Day 1, 15:00", title: "Automatic Alert Triggered", desc: "Sentinel Crop's system automatically flags the event and notifies the farmer via SMS." },
        { time: "Day 2, 09:00", title: "Satellite Verification Started", desc: "Sentinel-2 satellite pass captures NDVI images of the affected area." },
        { time: "Day 2, 14:00", title: "Damage Confirmed", desc: "AI analysis confirms 75ha affected with average NDVI drop of 0.32 (significant stress)." },
        { time: "Day 2, 16:15", title: "Payment Processed", desc: "€45,000 (75ha × €600) transferred to bank account via SEPA." }
      ],
      benefitsTitle: "Potential Benefits",
      benefitsIntro: "This type of automated process could provide:",
      benefitCashFlow: "Funds available in time to cover emergency treatments and worker payments",
      benefitNoDebt: "No need for loans to cover the waiting period",
      benefitPeace: "Focus on recovery instead of negotiating with adjusters",
      benefitTransparent: "Every step visible in the dashboard with satellite image proof",
      principlesTitle: "Principles of Parametric Insurance",
      principleSpeed: "In agriculture, time is everything. Quick payments enable quick responses to damage.",
      principleTransparency: "Being able to see satellite data and weather records eliminates disputes.",
      principleFairness: "Objective measurements mean fair treatment regardless of farm size or location.",
      importantNote: "Important Note",
      importantNoteDesc: "This is an illustrative scenario for educational purposes. Actual results depend on specific policy terms, verified weather conditions, and available satellite data. Consult with our team to understand how it could work for your specific farm.",
      learnMoreTitle: "Want to Learn More?",
      learnMoreDesc: "Discover how parametric insurance could work for your farm. Request information with no commitment.",
      viewPricing: "View Pricing",
      contact: "Contact"
    },
    // FrostProtectionAlerts
    frost: {
      badge: "Features",
      title: "Frost Protection Alerts: Early Warning Systems for Crop Protection",
      subtitle: "Scientific frost prediction through microclimate modeling and meteorological data from AEMET to protect vineyards, orchards and sensitive crops.",
      date: "December 2025",
      readTime: "6 min read",
      statAccuracy: "Temperature accuracy",
      statAdvance: "Advance notice",
      statPrediction: "Prediction accuracy",
      problemTitle: "The Problem of Late Frosts",
      problemP1: "Late spring frosts cause multimillion-dollar losses in European agriculture every year. According to the IPCC (2021), climate change is causing crops to begin their phenological development earlier, while the risk of late frosts remains or even increases in some regions.",
      problemP2: "In Spain, the April 2017 frosts caused estimated losses of more than €550 million in the wine sector alone (Source: COAG).",
      scienceTitle: "The Science of Frost Formation",
      scienceIntro: "Radiation frosts form when specific conditions occur that our system continuously monitors:",
      clearSkyTitle: "Clear sky",
      clearSkyDesc: "Cloudless nights allow heat to escape quickly into the atmosphere (radiative cooling). We monitor cloud cover every hour.",
      calmWindTitle: "Calm wind",
      calmWindDesc: "Wind speeds below 2 m/s allow cold air stratification near the ground. Critical threshold: <1.5 m/s.",
      dewPointTitle: "Low dew point",
      dewPointDesc: "When the dew point is below 0°C, there is a high probability of frost. It is the most reliable short-term indicator.",
      thermalInversionTitle: "Thermal inversion",
      thermalInversionDesc: "Cold air (denser) accumulates in low areas. We analyze topography to identify cold air pockets.",
      dataSourcesTitle: "Meteorological Data Sources",
      dataSourcesIntro: "Our system integrates multiple official data sources to maximize accuracy:",
      aemetSource: "Network of more than 800 automatic stations in Spain with hourly temperature, humidity, wind and radiation data.",
      ecmwfSource: "Numerical models from the European Centre for Medium-Range Weather Forecasts, world reference in weather prediction.",
      privateSource: "Integration of farmer-owned weather stations for local calibration.",
      microclimateTitle: "Microclimate Modeling",
      microclimateIntro: "The general AEMET forecast is not sufficient for a specific parcel. Therefore we apply microclimate corrections based on:",
      correctionFactorsTitle: "Microclimate Correction Factors",
      corrections: [
        { factor: "Elevation above sea level", value: "-0.65°C per 100m altitude" },
        { factor: "Slope and orientation", value: "±0.5°C depending on exposure" },
        { factor: "Proximity to water bodies", value: "+1 to +3°C moderating effect" },
        { factor: "Cold air drainage", value: "-1 to -4°C in valley bottoms" },
        { factor: "Soil type and coverage", value: "±0.5°C depending on albedo" }
      ],
      thresholdsTitle: "Crop Damage Thresholds",
      thresholdsIntro: "Not all crops are equally sensitive to cold. Our system knows the critical thresholds for each crop and phenological stage:",
      thresholdsTableTitle: "Critical Damage Temperatures",
      thCrop: "Crop",
      thStage: "Phenological stage",
      thTemp: "Critical temp.",
      crops: [
        { crop: "Grapevine", stage: "Bud break", temp: "-2°C to -3°C" },
        { crop: "Almond", stage: "Flowering", temp: "-2°C" },
        { crop: "Olive", stage: "Flowering", temp: "-1°C to -2°C" },
        { crop: "Cherry", stage: "Fruit set", temp: "-1°C" },
        { crop: "Citrus", stage: "Young fruit", temp: "-2°C to -3°C" }
      ],
      alertSystemTitle: "Multi-level Alert System",
      alertSystemIntro: "Alerts are classified according to probability and expected severity:",
      alertWatch: "30-50% probability of frost in the next 72h",
      alertWarning: "50-80% probability of frost in the next 48h",
      alertAlarm: ">80% probability of imminent frost (12-24h)",
      protectionTitle: "Recommended Protection Methods",
      protectionIntro: "Along with each alert, the system suggests appropriate protection methods:",
      protIrrigation: "Water releases heat when freezing (latent heat of fusion)",
      protWindMachines: "Mix warm upper air with cold air near the ground",
      protHeaters: "For moderate frosts in small areas",
      protCovers: "Physical protection for high-value crops",
      referencesTitle: "Scientific References"
    },
    // SmartIrrigationFAO56
    irrigation: {
      badge: "Features",
      title: "Smart Irrigation with FAO-56: Scientific Water Management",
      subtitle: "The FAO-56 Penman-Monteith equation is the international standard for calculating evapotranspiration. Learn how we apply it to optimize irrigation and save water.",
      date: "December 2025",
      readTime: "9 min read",
      statWater: "Water savings",
      statMethodology: "Standard methodology",
      statRecommendations: "Recommendations",
      evapTitle: "What is Evapotranspiration?",
      evapP1: "Evapotranspiration (ET) is the amount of water that passes to the atmosphere through evaporation from soil and transpiration from plants. Knowing the ET is fundamental to determine how much water a crop actually needs.",
      evapP2: "According to the FAO (Food and Agriculture Organization of the United Nations), irrigation represents 70% of global freshwater use. Optimizing irrigation not only saves water but improves production by avoiding water stress and over-irrigation.",
      fao56Title: "The FAO-56 Penman-Monteith Standard",
      fao56Intro: "In 1998, the FAO published \"Irrigation and Drainage Paper No. 56\" which established the Penman-Monteith equation as the standard method for calculating reference evapotranspiration (ET₀):",
      equationTitle: "FAO-56 Penman-Monteith Equation",
      variablesTitle: "Required Meteorological Variables",
      variablesIntro: "To calculate daily ET₀ we need the following meteorological data, which we obtain from the AEMET station network:",
      solarTitle: "Solar Radiation",
      solarDesc: "Received solar energy. It is the main driver of evapotranspiration. Measured in MJ/m²/day or estimated from sunshine hours.",
      tempHumTitle: "Temperature and Humidity",
      tempHumDesc: "Maximum, minimum and mean temperature. Relative humidity to calculate vapor pressure deficit.",
      windTitle: "Wind Speed",
      windDesc: "Wind at 2 meters height. Higher wind = higher evapotranspiration by renewing humid air over leaves.",
      elevTitle: "Elevation and Latitude",
      elevDesc: "Needed to calculate atmospheric pressure and extraterrestrial radiation as a function of time of year.",
      kcTitle: "From ET₀ to ETc: Crop Coefficients (Kc)",
      kcIntro: "ET₀ represents a reference crop (grass). To obtain the actual crop evapotranspiration (ETc) we apply the crop coefficient Kc:",
      kcNote: "Where Kc varies according to crop and development stage",
      kcValuesTitle: "Kc Values by Phenological Stage",
      kcHeaders: ["Crop", "Kc initial", "Kc mid", "Kc end"],
      kcSource: "Source: FAO Irrigation and Drainage Paper No. 56, Table 12",
      dualKcTitle: "Dual Kc Method (Advanced)",
      dualKcP1: "For greater precision, especially in drip irrigation, we use the dual Kc method that separates crop transpiration (Kcb) from soil evaporation (Ke):",
      dualKcP2: "This allows adjusting recommendations based on irrigation system and degree of soil coverage, improving precision in tree crops with localized irrigation.",
      waterBalanceTitle: "Soil Water Balance",
      waterBalanceIntro: "The system maintains a daily soil water balance considering:",
      waterBalanceInputs: "Effective precipitation + Applied irrigation",
      waterBalanceOutputs: "Calculated ETc",
      waterBalanceStorage: "Available water in root zone",
      waterBalanceNote: "When available water depletion exceeds the stress threshold (typically 50-60% of retention capacity), an irrigation recommendation is generated.",
      integrationTitle: "Integration with Satellite Data",
      integrationIntro: "Sentinel Crop combines FAO-56 calculation with satellite data to validate and adjust recommendations:",
      integrationNDVI: "Confirms crop development stage and adjusts Kc",
      integrationNDWI: "Detects water stress signs before they are visible",
      integrationLST: "Validates actual evapotranspiration",
      benefitsTitle: "Documented Benefits",
      benefitsItems: [
        "20-30% water savings reported in literature vs over-irrigation (actual results vary per farm)",
        "Improved yields by avoiding water stress and waterlogging",
        "Reduced fungal diseases from excess moisture",
        "Compliance with water management plans in regulated areas"
      ],
      referencesTitle: "References"
    },
    // DigitalFieldNotebook
    notebook: {
      badge: "Features",
      title: "Digital Field Notebook: Complete Farm Activity Traceability",
      subtitle: "Satellite-validated activity logging that creates verifiable documentation for regulatory compliance, certifications, and profitability analysis.",
      date: "December 2025",
      readTime: "5 min read",
      statTraceability: "Digital traceability",
      statCompliance: "Regulatory compliance",
      statROI: "Profitability analysis",
      whatIsTitle: "What is a Digital Field Notebook?",
      whatIsP1: "The digital field notebook is the evolution of traditional paper records. It allows documenting all agricultural activities in a structured way, with automatic geolocation and validation through satellite data.",
      whatIsP2: "According to Regulation (EU) 2021/2116 on the financing, management and monitoring of the Common Agricultural Policy (CAP), farmers must maintain verifiable records of their agricultural practices.",
      activitiesTitle: "Types of Activities Recorded",
      phytoTitle: "Phytosanitary Treatments",
      phytoItems: ["Product applied and dose", "Date and time of application", "Weather conditions", "Pre-harvest interval"],
      fertTitle: "Fertilization",
      fertItems: ["Fertilizer type (N-P-K)", "Quantity per hectare", "Application method", "Nutrient balance"],
      opsTitle: "Crop Operations",
      opsItems: ["Sowing and planting", "Pruning and thinning", "Harvest and yields", "Tillage"],
      irrigTitle: "Irrigation",
      irrigItems: ["Water volume applied", "Irrigation duration", "System used", "Water efficiency"],
      satValidTitle: "Satellite Validation of Activities",
      satValidIntro: "What differentiates Sentinel Crop is validation through satellite data. Each recorded activity is correlated with Sentinel-2 satellite images to verify:",
      satValidItems: [
        "Detects if the intervention had an effect on vegetation",
        "Images of crop state pre and post activity",
        "AEMET data for climate context",
        "Verification that the activity was performed on the correct parcel"
      ],
      capTitle: "CAP Compliance and Conditionality",
      capIntro: "The new CAP 2023-2027 introduces reinforced conditionality requirements. The digital field notebook facilitates compliance with:",
      capItems: [
        "Good Agricultural and Environmental Conditions",
        "Statutory Management Requirements",
        "Documentation of sustainable practices",
        "Sustainable use of pesticides"
      ],
      organicTitle: "Organic Certification",
      organicIntro: "For farmers in organic production, the digital notebook generates the documentation required by Regulation (EU) 2018/848 on organic production:",
      organicItems: [
        "Record of all inputs used",
        "Complete production traceability",
        "Documentation for certification audits",
        "Clear separation between conventional and organic parcels"
      ],
      roiTitle: "ROI Analysis by Activity",
      roiIntro: "Each recorded activity is linked to its cost and the return on investment is analyzed:",
      roiExample: "Example: Fungicide Treatment",
      roiCost: "Treatment cost",
      roiNDVI: "Observed NDVI improvement",
      roiYield: "Estimated yield saved",
      roiCalculated: "Calculated ROI",
      exportTitle: "Export and Reports",
      exportIntro: "The field notebook allows exporting documentation in multiple formats:",
      exportItems: [
        "For banks, insurers and control bodies",
        "For own analysis and accounting",
        "Compatibility with agricultural machinery",
        "Integration with other management systems"
      ],
      regulatoryTitle: "Regulatory References"
    },
    // VariableRateFertilization
    vrt: {
      badge: "Features",
      title: "Variable Rate Fertilization: Precision Nutrient Management",
      subtitle: "How NDVI-based prescription maps from satellite analysis optimize fertilizer application, reducing costs and minimizing environmental impact.",
      date: "December 2025",
      readTime: "7 min read",
      statSavings: "Fertilizer savings",
      statResolution: "Spatial resolution",
      statFootprint: "Nitrogen footprint",
      whatIsTitle: "What is Variable Rate Fertilization (VRT)?",
      whatIsP1: "Variable Rate Technology (VRT) fertilization is a precision agriculture technique that adjusts the amount of fertilizer applied in each zone of the field according to the specific needs of the crop at that point.",
      whatIsP2: "According to studies by the Joint Research Centre (JRC) of the European Commission, precision agriculture can reduce nitrogen fertilizer use between 10% and 30% without affecting yields.",
      scienceTitle: "Scientific Basis: NDVI and Nutritional Status",
      scienceIntro: "The NDVI (Normalized Difference Vegetation Index) obtained from Sentinel-2 satellites is correlated with chlorophyll content and crop nutritional status:",
      correlationTitle: "NDVI - Nitrogen Correlation",
      correlationItems: [
        "NDVI < 0.4: Severe deficiency - High dose recommended",
        "NDVI 0.4-0.6: Moderate deficiency - Medium dose",
        "NDVI 0.6-0.8: Optimal status - Standard dose",
        "NDVI > 0.8: Vegetative excess - Reduce dose"
      ],
      processTitle: "Prescription Map Generation Process",
      processIntro: "Sentinel Crop generates prescription maps following a scientifically validated process:",
      processSteps: [
        "Sentinel-2 image acquisition: Multispectral images every 5 days with 10 meter resolution",
        "Atmospheric correction: Level-2A processing to obtain actual surface reflectance values",
        "Index calculation: NDVI, NDRE (Red Edge) for greater nitrogen sensitivity",
        "Field zonation: Division into homogeneous management zones using clustering algorithms",
        "Calibration with soil analysis: Dose adjustment based on available soil data",
        "Map generation: Export in format compatible with agricultural machinery (ISO-XML, Shapefile)"
      ],
      ndreTitle: "NDRE Index: Better for Nitrogen",
      ndreIntro: "In addition to NDVI, we use the NDRE (Normalized Difference Red Edge) index which is more sensitive to variations in chlorophyll and nitrogen content:",
      ndreNote: "The Sentinel-2 Red Edge band (705nm) detects chlorophyll variations before they are visible in traditional NDVI.",
      machineryTitle: "Machinery Compatibility",
      machineryIntro: "Prescription maps are exported in formats compatible with the main variable application systems:",
      benefitsTitle: "Documented Benefits",
      economicTitle: "Economic",
      economicItems: ["15-25% fertilizer savings reported in literature (actual results vary)", "ROI 3-5x reported in first year by some studies — not guaranteed for every farm", "Lower application cost", "Maintained or improved yields"],
      environmentalTitle: "Environmental",
      environmentalItems: ["Reduced nitrate leaching", "Lower N₂O emissions (greenhouse gas)", "Nitrates Directive compliance", "Improved water quality"],
      useCaseTitle: "Use Case: Winter Cereals",
      useCaseIntro: "For cereal crops (wheat, barley, triticale), variable fertilization is especially effective in top-dressing:",
      regulatoryTitle: "Regulatory Compliance",
      regulatoryIntro: "Variable fertilization helps comply with European and national regulations:",
      referencesTitle: "Scientific References",
      ctaTitle: "Optimize your fertilization",
      ctaDesc: "Generate prescription maps based on real satellite data from your parcel.",
      ctaButton: "View Variable Fertilization"
    },
    // ProfessionalReports
    reports: {
      badge: "Features",
      title: "Professional Agricultural Reports for Banks and Insurance",
      subtitle: "Verified documentation with satellite data to support financing applications, insurance claims and quality certifications.",
      date: "December 2025",
      readTime: "4 min read",
      statFormat: "Professional format",
      statVerified: "Satellite data",
      statAccepted: "By financial institutions",
      whyTitle: "Why Reports with Satellite Data?",
      whyIntro: "Financial institutions and insurers increasingly value documentation backed by objective data. Sentinel Crop reports provide:",
      whyItems: [
        "Data from official satellites of the European Space Agency",
        "Verifiable history of crop evolution",
        "Standardized format recognized by the sector",
        "Recent data with verifiable capture date"
      ],
      typesTitle: "Available Report Types",
      financingTitle: "Financing Report",
      financingItems: ["SIGPAC parcel identification", "3-year NDVI history", "Historical yields", "Farm valuation", "Income projection"],
      insuranceTitle: "Insurance Report",
      insuranceItems: ["Pre-incident crop status", "Damage documentation", "Before/after comparison", "Weather data", "Loss quantification"],
      managementTitle: "Management Report",
      managementItems: ["Activity summary", "Cost analysis", "ROI per intervention", "Parcel comparison", "Technical recommendations"],
      capTitle: "CAP Report",
      capItems: ["Area declaration", "Eco-scheme compliance", "GAEC practices evidence", "Conditionality documentation", "Activity records"],
      contentTitle: "Financing Report Content",
      contentIntro: "The report for financial institutions includes all information needed to support agricultural credit applications:",
      structureTitle: "Report Structure",
      structureSteps: [
        { title: "Farm Identification", desc: "SIGPAC data, total area, crops, geographic location" },
        { title: "Satellite Analysis", desc: "Sentinel-2 images, NDVI maps, temporal evolution" },
        { title: "Yield History", desc: "Last seasons production, regional comparison" },
        { title: "Risk Analysis", desc: "Climate history, identified vulnerabilities" },
        { title: "Economic Valuation", desc: "Income estimate, production costs, gross margin" }
      ],
      sourcesTitle: "Official Data Sources",
      sourcesIntro: "All reports use exclusively official and verifiable data sources:",
      acceptanceTitle: "Acceptance by Institutions",
      acceptanceIntro: "Sentinel Crop reports are designed following the requirements of the main entities in the Spanish agricultural sector:",
      generationTitle: "Generation Process",
      generationSteps: [
        "Parcel selection: Choose the parcels to include in the report",
        "Report type: Select format according to recipient",
        "Time period: Define the date range to analyze",
        "Automatic generation: The system collects and formats the data",
        "PDF download: Professional report ready to present"
      ],
      addedValueTitle: "Added Value",
      addedValueIntro: "Beyond documentation, reports provide added value:",
      addedValueItems: [
        "Improves the professional image of the farm",
        "Facilitates communication with technicians and advisors",
        "Allows comparing evolution between seasons",
        "Serves as a basis for decision making"
      ],
      ctaTitle: "Generate your professional report",
      ctaDesc: "Satellite-verified documentation for banks, insurers and certifications.",
      ctaButton: "Go to Reports"
    },
    // FireRiskMonitoring
    fire: {
      badge1: "Technology",
      badge2: "NASA Data",
      title: "Fire-Risk Monitoring with NASA FIRMS: Satellite-Assisted Agricultural Awareness",
      subtitle: "Leveraging NASA's Fire Information for Resource Management System to surface satellite-derived hotspot signals near your parcels. Not an emergency service and not a substitute for official fire alerts.",
      date: "December 28, 2025",
      readTime: "7 min read",
      keyTechTitle: "Key Technology",
      keyTechDesc: "NASA FIRMS uses MODIS and VIIRS satellite sensors to detect thermal anomalies globally, providing near-real-time hotspot signals within ~3 hours of satellite overpass at 375m resolution. These are satellite-derived signals that require validation, not confirmed active fires.",
      howItWorksTitle: "How NASA FIRMS Works",
      howItWorksIntro: "The Fire Information for Resource Management System (FIRMS) distributes Near Real-Time (NRT) active fire data from NASA's polar-orbiting satellites. The system uses two primary sensors:",
      modisTitle: "MODIS Sensor",
      modisItems: ["1km spatial resolution", "Terra & Aqua satellites", "4 daily overpasses", "Operational since 2000"],
      viirsTitle: "VIIRS Sensor",
      viirsItems: ["375m spatial resolution", "Suomi NPP & NOAA-20", "Higher sensitivity", "Better small-hotspot sensitivity"],
      integrationTitle: "Sentinel Crop Integration",
      integrationIntro: "Our platform queries the NASA FIRMS API to monitor satellite-derived hotspot signals near your registered parcels:",
      proximityTitle: "Proximity Monitoring",
      proximityDesc: "Configurable radius from 1km to 50km around each parcel, with default 5km for early warning.",
      riskLevelTitle: "Risk Level Classification",
      riskLevelDesc: "Critical (0-2km), High (2-5km), Moderate (5-15km), Low (15-50km) based on distance and fire intensity.",
      frpTitle: "Fire Radiative Power (FRP)",
      frpDesc: "Measured in megawatts, FRP indicates fire intensity and potential spread rate.",
      alertTitle: "Alert System",
      alertIntro: "When satellite hotspots are detected near your parcels, our system automatically:",
      alertItems: [
        "Sends SMS alerts to registered phone numbers (critical risk)",
        "Triggers email notifications with hotspot location and details",
        "Updates the dashboard with a satellite hotspot map overlay",
        "Logs historical hotspot events for insurance documentation",
        "Calculates potential impact based on wind direction and terrain"
      ],
      insuranceTitle: "Insurance Integration",
      insuranceDesc: "Satellite-derived hotspot data from NASA FIRMS serves as objective evidence for parametric insurance claims. Our platform automatically captures satellite imagery, hotspot coordinates, observation time, and radiative power measurements — all verifiable through NASA's public archive. Hotspot signals are not a substitute for official fire confirmation by emergency services.",
      attributionTitle: "Data Sources & Attribution",
      attributionDesc: "Fire data provided by NASA FIRMS (Fire Information for Resource Management System), a component of NASA's Land, Atmosphere Near real-time Capability for EOS (LANCE). Data is available under NASA's open data policy for scientific and operational use."
    },
    // FloodRiskAssessment
    flood: {
      badge1: "Technology",
      badge2: "Copernicus Data",
      title: "Flood Risk Assessment with Copernicus Global Surface Water",
      subtitle: "Leveraging 40 years of Landsat satellite imagery processed by the Joint Research Centre (JRC) to understand historical water dynamics and flood risk for your agricultural land.",
      date: "December 28, 2025",
      readTime: "8 min read",
      datasetTitle: "Global Surface Water Dataset",
      datasetDesc: "The JRC Global Surface Water dataset maps the location and temporal distribution of water surfaces at 30m resolution, using over 4.7 million Landsat satellite images from 1984 to present.",
      measureTitle: "What We Measure",
      occurrenceTitle: "Water Occurrence",
      occurrenceDesc: "Percentage of time water was present from 1984-2021.",
      occurrenceItems: ["0%: Never flooded", "1-25%: Rare flooding", "25-50%: Seasonal water", "50%+: Permanent water body"],
      recurrenceTitle: "Water Recurrence",
      recurrenceDesc: "Inter-annual recurrence of water presence.",
      recurrenceItems: ["High: Floods most years", "Medium: Floods some years", "Low: Rare flood events", "None: No historical flooding"],
      riskTitle: "Risk Level Classification",
      riskIntro: "Sentinel Crop analyzes multiple factors to determine flood risk:",
      highRiskTitle: "High Risk",
      highRiskDesc: "Water occurrence greater than 15% or within 100m of permanent water body. Recommend flood insurance and drainage improvements.",
      moderateRiskTitle: "Moderate Risk",
      moderateRiskDesc: "Water occurrence 5-15% or seasonal flooding patterns detected. Consider drainage assessment and crop selection.",
      lowRiskTitle: "Low/Minimal Risk",
      lowRiskDesc: "Water occurrence below 5% with no significant flooding history. Standard agricultural practices recommended.",
      accessTitle: "How We Access the Data",
      accessIntro: "Our platform connects to the Copernicus Data Space Ecosystem (CDSE) via their APIs:",
      accessItems: [
        "Direct access to Copernicus thematic products",
        "Pixel-level analysis at your parcel coordinates",
        "Data extracted on-demand for each query",
        "Access to 40 years of water dynamics data"
      ],
      insuranceTitle: "Parametric Insurance Application",
      insuranceDesc: "Flood risk assessments from the Global Surface Water dataset can trigger parametric insurance policies when combined with real-time Sentinel-1 SAR flood detection. Historical water occurrence establishes baseline risk, while active monitoring detects current flood events.",
      metricsTitle: "Additional Metrics",
      seasonality: "Peak flood months",
      distance: "Nearest water body",
      trend: "Increasing/decreasing",
      attributionTitle: "Data Attribution",
      attributionDesc: "Global Surface Water dataset provided by the European Commission's Joint Research Centre (JRC) under the Copernicus Programme. Based on Landsat satellite imagery processed using Google Earth Engine. Citation: Pekel et al., 2016. High-resolution mapping of global surface water and its long-term changes. Nature."
    },
    // CarbonMonitoring
    carbon: {
      badge1: "Sustainability",
      badge2: "Blockchain",
      title: "Carbon Monitoring & Credit Tokenization for Agriculture",
      subtitle: "Combining Copernicus satellite data for biomass carbon estimation with blockchain technology to create verifiable, tradeable carbon credits from sustainable agricultural practices.",
      date: "December 28, 2025",
      readTime: "9 min read",
      opportunityTitle: "The Opportunity",
      opportunityDesc: "Agricultural land can sequester 1-5 tonnes of CO2 per hectare annually through improved practices. With carbon credits trading at €50-100/tonne, this represents significant revenue potential for farmers.",
      measurementTitle: "Satellite-Based Carbon Measurement",
      measurementIntro: "Sentinel Crop uses the Copernicus Global Biomass Carbon dataset to estimate carbon stocks:",
      aboveGroundTitle: "Above-Ground Carbon",
      aboveGroundDesc: "Biomass in vegetation, trees, and crop residues.",
      aboveGroundItems: ["Measured in tonnes C/hectare", "Updated seasonally", "100m resolution", "Validated against field measurements"],
      soilCarbonTitle: "Soil Organic Carbon",
      soilCarbonDesc: "Carbon stored in soil organic matter.",
      soilCarbonItems: ["Estimated from soil models", "Based on LUCAS soil survey", "Accounts for soil type", "Climate zone adjusted"],
      sequestrationTitle: "Carbon Sequestration Potential",
      sequestrationIntro: "Based on your parcel's current carbon stocks and land use, we calculate sequestration potential:",
      steps: [
        { title: "Baseline Assessment", desc: "Current carbon stocks measured via satellite (above-ground + soil organic carbon)." },
        { title: "Practice Assessment", desc: "Analysis of current practices: cover crops, tillage, fertilization, crop rotation recorded in Field Notebook." },
        { title: "Sequestration Calculation", desc: "Annual CO2e sequestration estimated using IPCC Tier 2 methodology adjusted for climate zone." },
        { title: "Credit Generation", desc: "Verified tonnes CO2e converted to carbon credits eligible for tokenization." }
      ],
      blockchainTitle: "Blockchain Tokenization",
      whyBlockchainTitle: "Why Blockchain?",
      whyBlockchainDesc: "Public ledgers provide tamper-evident, transparent records of token creation, ownership, and transfer. This is an experimental module — Sentinel Crop does not issue certified carbon credits and any output is an activity-based estimate, not a certified carbon credit.",
      baseIntro: "Sentinel Crop's carbon tokenization system uses Base (Ethereum L2) for low-cost, fast transactions:",
      erc20Title: "ERC-20 Tokens",
      erc20Desc: "1 token = 1 tonne CO2e",
      onChainTitle: "On-Chain Proof",
      onChainDesc: "Satellite data hashes stored",
      tradeableTitle: "Tradeable",
      tradeableDesc: "Sell on carbon markets",
      processTitle: "The Process",
      processSteps: [
        "Register Parcel: Add your agricultural land to Sentinel Crop with coordinates and crop type.",
        "Baseline Measurement: Satellite analysis establishes current carbon stocks.",
        "Practice Verification: Field Notebook records sustainable practices (cover crops, no-till, etc.).",
        "Annual Monitoring: Yearly satellite assessment measures carbon stock changes.",
        "Credit Calculation: Net sequestration converted to carbon credits using IPCC methodology.",
        "Tokenization: Credits minted as blockchain tokens on Base network.",
        "Sale/Retirement: Trade tokens on carbon markets or retire for sustainability claims."
      ],
      statusTitle: "Current Status",
      statusDesc: "Carbon credit tokenization is available in beta. Smart contracts are deployed on Base Sepolia testnet for testing. Production deployment pending regulatory clarity and third-party verification partnerships.",
      sourcesTitle: "Data Sources"
    }
  }
};
